import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Listing, getShowSensitiveContent } from 'models/Listing';
import useShowEnquireAndWatchlist from 'hooks/useShowEnquireAndWatchlist';
import { Button } from 'ncoded-component-library';
import ChatIcon from 'icons/Chat.icon';
import useOpenEnquire from 'hooks/useOpenEnquire';
import AuthContext from 'providers/Auth/Auth.context';
import { Link } from 'react-router-dom';
import utils, {
  checkIsPublic,
  getBuyingPortfolio,
  getIsListingOwner,
  getPortfolioRoute,
  getResolveUrl,
} from 'utils';
import StarIcon from 'icons/Star.icon';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useWatchlist from 'hooks/useWatchlist';
import LinkToIcon from 'icons/LinkTo.icon';
import ShareButton from 'components/ShareButton';

import './ListingActions.styles.scss';

type ListingActionsProps = {
  className?: string;
  listing: Listing;
  showMessageBrokerButton?: boolean;
  onWatchlisted: (isWatchlisted: boolean) => void;
};

const ListingActions: React.FC<ListingActionsProps> = (props) => {
  const {
    className,
    listing,
    showMessageBrokerButton = false,
    onWatchlisted,
  } = props;

  const { isLoggedIn } = useContext(AuthContext);
  const { currentUser, isSuperAdmin } = useContext(CurrentUserContext);

  const {
    _id: listingId,
    listingEnquiryId,
    isEnquired,
    userId,
    isCoOwner,
    dealRoomId,
    stage,
    industry,
    slugName,
    type,
    isWatchlisted,
    sessionId,
    listingTitle,
  } = listing || {};

  const isListingOwner = getIsListingOwner(currentUser?._id, userId, isCoOwner);

  const isListingOwnerOrSuperAdmin = isSuperAdmin || isListingOwner;

  const redirectionListingLink = utils.composeBusinessUrl(
    industry,
    slugName,
    type,
  );

  const showSensitiveContent = useMemo(
    () => getShowSensitiveContent(listing),
    [listing],
  );

  const { t } = useTranslation();

  const { showEnquire, showWatchlist } = useShowEnquireAndWatchlist(listing);

  const openEnquire = useOpenEnquire();

  const { loading: watchlistRequestLoading, onWatchlist } = useWatchlist(
    listingId,
    isWatchlisted,
    onWatchlisted,
  );

  const EnquireButton = useMemo(
    () =>
      showEnquire && (
        <Button
          variant="outline"
          icon={ChatIcon}
          onClick={() => openEnquire(listing)}
        >
          {t('enquire')}
        </Button>
      ),
    [listing, openEnquire, showEnquire, t],
  );

  const PortfolioLink = useMemo(
    () =>
      (isListingOwnerOrSuperAdmin || isEnquired) && (
        <Link
          className="ncoded-button ncoded-button--link"
          to={getPortfolioRoute(
            !isListingOwnerOrSuperAdmin,
            dealRoomId,
            listingEnquiryId,
          )}
        >
          <LinkToIcon />
          {t('portfolio')}
        </Link>
      ),
    [dealRoomId, isEnquired, isListingOwnerOrSuperAdmin, listingEnquiryId, t],
  );

  const MessageBrokerLink = useMemo(
    () =>
      showMessageBrokerButton &&
      sessionId &&
      listingEnquiryId && (
        <Link
          className="ncoded-button ncoded-button--link"
          to={`${getBuyingPortfolio(
            listingEnquiryId,
            'messaging',
          )}/${sessionId}`}
        >
          <ChatIcon />
          {t('message')}
        </Link>
      ),
    [listingEnquiryId, sessionId, showMessageBrokerButton, t],
  );

  const WatchlistButton = useMemo(
    () =>
      isLoggedIn &&
      showWatchlist && (
        <Button
          className={classNames({
            fill: isWatchlisted,
          })}
          variant="link"
          icon={StarIcon}
          disabled={isListingOwner || watchlistRequestLoading}
          onClick={onWatchlist}
        >
          {t('shortlist')}
        </Button>
      ),
    [
      isListingOwner,
      isLoggedIn,
      isWatchlisted,
      onWatchlist,
      showWatchlist,
      t,
      watchlistRequestLoading,
    ],
  );

  const SocialShareButton = useMemo(
    () =>
      checkIsPublic(stage) ? (
        <ShareButton
          url={getResolveUrl(redirectionListingLink)}
          listingId={listingId}
          listingName={listingTitle}
        />
      ) : null,
    [stage, redirectionListingLink, listingId, listingTitle],
  );

  const classes = classNames('listing-actions', className);

  return (
    <div className={classes}>
      {showSensitiveContent && EnquireButton}
      {PortfolioLink}
      {MessageBrokerLink}
      {showSensitiveContent && WatchlistButton}
      {SocialShareButton}
    </div>
  );
};

export default ListingActions;
